import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Users,
    Calculator,
    Save,
    Eye,
    EyeOff,
    AlertCircle,
    Zap,
    Search,
    User as UserIcon,
    ChevronRight,
    Shield,
    BarChart2,
    UserCheck,
    Briefcase,
} from 'lucide-react';
import { fetchPeerConfig, updatePeerConfig } from '../../../../redux/actions/peerEvaluationConfigActions';
import { fetchCampagnesRequest } from '../../../../redux/actions/campagnesActions';
import { generatePeerProposalsRequest, fetchSkillEvalsByUserRequest } from '../../../../redux/actions/skillEvaluationActions';
import { getAllUsersRequest } from '../../../../redux/actions/userActions';
import { fetchJobSkillsRequest } from '../../../../redux/actions/jobSkillActions';
import './ParamEvaluation360.css';

const ParamEvaluation360 = ({ embedded = false }) => {
    const dispatch = useDispatch();

    const { config, loading } = useSelector((state) => state.peerEvaluationConfig);
    const { list: campagnes } = useSelector((state) => state.campagnes);
    const { users } = useSelector((state) => state.user);
    const { skillEvaluations, loading: loadingEvals } = useSelector((state) => state.skillEvaluation);
    const { jobSkills, loading: loadingJobSkills } = useSelector((state) => state.jobSkill);

    const [activeTab, setActiveTab] = useState('config');
    const [selectedCampagneId, setSelectedCampagneId] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        calculationMethod: 'AVERAGE',
        enablePeers: true,
        peerSelectionPerimeter: 'SAME_ENTITY',
        peerNbEvaluators: 4,
        peerWeight: 1.0,
        enableCollabs: false,
        collabNbEvaluators: 2,
        collabWeight: 1.0,
        enableManager: true,
        managerWeight: 1.0,
        isAnonymous: false,
    });

    useEffect(() => {
        dispatch(fetchPeerConfig());
        dispatch(fetchCampagnesRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    useEffect(() => {
        if (config) {
            setFormData({
                calculationMethod: config.calculationMethod || 'AVERAGE',
                enablePeers: config.enablePeers ?? true,
                peerSelectionPerimeter: config.peerSelectionPerimeter || 'SAME_ENTITY',
                peerNbEvaluators: config.peerNbEvaluators || 4,
                peerWeight: config.peerWeight || 1.0,
                enableCollabs: config.enableCollabs ?? false,
                collabNbEvaluators: config.collabNbEvaluators || 2,
                collabWeight: config.collabWeight || 1.0,
                enableManager: config.enableManager ?? true,
                managerWeight: config.managerWeight || 1.0,
                isAnonymous: config.isAnonymous ?? false,
            });
        }
    }, [config]);

    useEffect(() => {
        if (selectedUserId) {
            dispatch(fetchSkillEvalsByUserRequest(selectedUserId));
            const user = users?.find(u => u.id === parseInt(selectedUserId));
            if (user?.jobId) dispatch(fetchJobSkillsRequest(user.jobId));
        }
    }, [selectedUserId, dispatch, users]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const toggle = (name) => setFormData(prev => ({ ...prev, [name]: !prev[name] }));

    const handleSave = () => {
        const payload = {
            calculationMethod: formData.calculationMethod,
            enablePeers: !!formData.enablePeers,
            peerSelectionPerimeter: formData.peerSelectionPerimeter,
            peerNbEvaluators: parseInt(formData.peerNbEvaluators) || 0,
            peerWeight: parseFloat(formData.peerWeight) || 0,
            enableCollabs: !!formData.enableCollabs,
            collabNbEvaluators: parseInt(formData.collabNbEvaluators) || 0,
            collabWeight: parseFloat(formData.collabWeight) || 0,
            enableManager: !!formData.enableManager,
            managerWeight: parseFloat(formData.managerWeight) || 0,
            isAnonymous: !!formData.isAnonymous,
        };
        dispatch(updatePeerConfig(payload));
    };

    const handleLaunchAssignments = () => {
        if (!selectedCampagneId) return;
        dispatch(generatePeerProposalsRequest(selectedCampagneId));
    };

    const filteredUsers = users?.filter(u => {
        const fullSearch = ((u.firstName || '') + ' ' + (u.lastName || '') + ' ' + (u.jobName || '')).toLowerCase();
        return fullSearch.includes(searchTerm.toLowerCase());
    }).slice(0, 10);

    const hasPeerSkills = jobSkills?.some(js => {
        const code = (js.modeEvaluationCode || '').toUpperCase();
        const nom = (js.modeEvaluationNom || '').toLowerCase();
        return code.includes('PAIRES') || code.includes('360') || nom.includes('paires') || nom.includes('360');
    });

    const peerAssignments = skillEvaluations?.filter(e =>
        e.campagneId === parseInt(selectedCampagneId) && e.evaluateurId && e.userId === parseInt(selectedUserId)
    );

    return (
        <div className={`pe-page${embedded ? ' pe-page--embedded' : ''}`}>

            {/* ── Header ── */}
            <div className="pe-header">
                <div className="pe-header-left">
                    <h1>Paramétrage 360°</h1>
                    <p className="pe-subtitle">Configurez les règles d'évaluation multi-sources</p>
                </div>
                <div className="pe-header-actions">
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="pe-btn pe-btn-primary"
                    >
                        <Save size={14} /> {loading ? 'En cours...' : 'Enregistrer'}
                    </button>
                </div>
            </div>

            {/* ── Campaign bar ── */}
            <div className="pe-campaign-bar">
                <span className="pe-campaign-label">Campagne</span>
                <select
                    className="pe-campaign-select"
                    value={selectedCampagneId}
                    onChange={(e) => setSelectedCampagneId(e.target.value)}
                >
                    <option value="">— Choisir une campagne —</option>
                    {campagnes?.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
                <div className="pe-campaign-bar-sep" />
                <button
                    onClick={handleLaunchAssignments}
                    disabled={!selectedCampagneId}
                    className="pe-btn pe-btn-accent"
                >
                    <Zap size={14} /> Lancer le tirage au sort
                </button>
            </div>

            {/* ── Tabs ── */}
            <div className="pe-tabs">
                <button
                    className={`pe-tab${activeTab === 'config' ? ' active' : ''}`}
                    onClick={() => setActiveTab('config')}
                >
                    <span className="pe-tab-dot" />
                    Règles d'évaluation
                </button>
                <button
                    className={`pe-tab${activeTab === 'consult' ? ' active' : ''}`}
                    onClick={() => setActiveTab('consult')}
                >
                    <span className="pe-tab-dot" />
                    Consultation des paires
                </button>
            </div>

            {/* ════ TAB CONFIG ════ */}
            {activeTab === 'config' && (
                <div className="pe-config-grid">

                    {/* COL 1 — Sources */}
                    <div>
                        <div className="pe-card">
                            <div className="pe-card-header">
                                <div className="pe-card-header-icon">
                                    <Users size={14} />
                                </div>
                                Sources d'évaluateurs
                            </div>
                            <div className="pe-card-body" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

                                {/* Manager */}
                                <div className={`pe-source-block${formData.enableManager ? ' is-active' : ''}`}>
                                    <div className="pe-source-header" onClick={() => toggle('enableManager')}>
                                        <div className="pe-source-icon manager">
                                            <Briefcase size={14} />
                                        </div>
                                        <div className="pe-source-title">
                                            <span className="pe-source-name">N+1 — Manager</span>
                                            <span className="pe-source-desc">Évaluation hiérarchique directe</span>
                                        </div>
                                        <button
                                            className={`pe-source-toggle${formData.enableManager ? ' on' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); toggle('enableManager'); }}
                                            type="button"
                                        />
                                    </div>
                                    <div className={`pe-source-fields${formData.enableManager ? '' : ' hidden'}`}>
                                        <div className="pe-mini-field">
                                            <span className="pe-mini-label">Poids (%)</span>
                                            <input
                                                type="number" step="1" name="managerWeight"
                                                value={formData.managerWeight} onChange={handleChange}
                                                className="pe-mini-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Collabs */}
                                <div className={`pe-source-block${formData.enableCollabs ? ' is-active' : ''}`}>
                                    <div className="pe-source-header" onClick={() => toggle('enableCollabs')}>
                                        <div className="pe-source-icon collab">
                                            <UserCheck size={14} />
                                        </div>
                                        <div className="pe-source-title">
                                            <span className="pe-source-name">N-1 — Collaborateurs</span>
                                            <span className="pe-source-desc">Évaluation par les équipes</span>
                                        </div>
                                        <button
                                            className={`pe-source-toggle${formData.enableCollabs ? ' on' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); toggle('enableCollabs'); }}
                                            type="button"
                                        />
                                    </div>
                                    <div className={`pe-source-fields${formData.enableCollabs ? '' : ' hidden'}`}>
                                        <div className="pe-mini-field">
                                            <span className="pe-mini-label">Nombre de collaborateurs</span>
                                            <input
                                                type="number" name="collabNbEvaluators"
                                                value={formData.collabNbEvaluators} onChange={handleChange}
                                                className="pe-mini-input"
                                            />
                                        </div>
                                        <div className="pe-mini-field">
                                            <span className="pe-mini-label">Poids (%)</span>
                                            <input
                                                type="number" step="1" name="collabWeight"
                                                value={formData.collabWeight} onChange={handleChange}
                                                className="pe-mini-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Pairs */}
                                <div className={`pe-source-block${formData.enablePeers ? ' is-active' : ''}`}>
                                    <div className="pe-source-header" onClick={() => toggle('enablePeers')}>
                                        <div className="pe-source-icon peer">
                                            <Users size={14} />
                                        </div>
                                        <div className="pe-source-title">
                                            <span className="pe-source-name">Pairs — Même niveau</span>
                                            <span className="pe-source-desc">Évaluation horizontale 360°</span>
                                        </div>
                                        <button
                                            className={`pe-source-toggle${formData.enablePeers ? ' accent' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); toggle('enablePeers'); }}
                                            type="button"
                                        />
                                    </div>
                                    <div className={`pe-source-fields${formData.enablePeers ? '' : ' hidden'}`}>
                                        <div className="pe-mini-field" style={{ flex: '2 1 130px' }}>
                                            <span className="pe-mini-label">Périmètre</span>
                                            <select
                                                name="peerSelectionPerimeter"
                                                value={formData.peerSelectionPerimeter}
                                                onChange={handleChange}
                                                className="pe-mini-select"
                                            >
                                                <option value="SAME_ENTITY">Même entité</option>
                                                <option value="DIFF_ENTITY">Toute l'entreprise</option>
                                            </select>
                                        </div>
                                        <div className="pe-mini-field">
                                            <span className="pe-mini-label">Nombre de pairs</span>
                                            <input
                                                type="number" name="peerNbEvaluators"
                                                value={formData.peerNbEvaluators} onChange={handleChange}
                                                className="pe-mini-input"
                                            />
                                        </div>
                                        <div className="pe-mini-field">
                                            <span className="pe-mini-label">Poids (%)</span>
                                            <input
                                                type="number" step="1" name="peerWeight"
                                                value={formData.peerWeight} onChange={handleChange}
                                                className="pe-mini-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* TOTAL WARNING */}
                                {(() => {
                                    const total = (formData.enableManager ? parseFloat(formData.managerWeight || 0) : 0) +
                                                  (formData.enableCollabs ? parseFloat(formData.collabWeight || 0) : 0) +
                                                  (formData.enablePeers ? parseFloat(formData.peerWeight || 0) : 0);
                                    
                                    if (total > 100) {
                                        return (
                                            <div style={{ marginTop: '10px', padding: '10px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px', color: '#b91c1c', fontSize: '12px' }}>
                                                <AlertCircle size={14} />
                                                <span><strong>Attention :</strong> Le total des poids ({total}%) dépasse les 100%.</span>
                                            </div>
                                        );
                                    }
                                    if (formData.calculationMethod === 'WEIGHTED_AVERAGE' && total > 0 && total !== 100) {
                                         return (
                                            <div style={{ marginTop: '10px', padding: '10px', background: '#f9fefd', borderRadius: '8px', border: '1px solid #f9fefd', display: 'flex', alignItems: 'center', gap: '8px', color: '#455584', fontSize: '12px' }}>
                                                <AlertCircle size={14} />
                                                <span>Note : Le total est de {total}%. Pour une moyenne pondérée classique, il est conseillé d'arriver à 100%.</span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}

                            </div>
                        </div>
                    </div>

                    {/* COL 2 — Formule + Confidentialité */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

                        {/* Formule */}
                        <div className="pe-card">
                            <div className="pe-card-header">
                                <div className="pe-card-header-icon">
                                    <Calculator size={14} />
                                </div>
                                Formule de calcul
                            </div>
                            <div className="pe-card-body">
                                <div className="pe-formula-options">
                                    {[
                                        { value: 'AVERAGE', label: 'Moyenne simple', hint: 'Toutes les sources ont le même poids' },
                                        { value: 'WEIGHTED_AVERAGE', label: 'Moyenne pondérée', hint: 'Selon les poids définis par source' },
                                    ].map(opt => (
                                        <div
                                            key={opt.value}
                                            className={`pe-formula-option${formData.calculationMethod === opt.value ? ' selected' : ''}`}
                                            onClick={() => setFormData(p => ({ ...p, calculationMethod: opt.value }))}
                                        >
                                            <div className="pe-formula-radio">
                                                <div className="pe-formula-radio-dot" />
                                            </div>
                                            <div className="pe-formula-text">
                                                <span className="pe-formula-name">{opt.label}</span>
                                                <span className="pe-formula-hint">{opt.hint}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Confidentialité */}
                        <div className="pe-card">
                            <div className="pe-card-header">
                                <div className="pe-card-header-icon">
                                    <Shield size={14} />
                                </div>
                                Confidentialité
                            </div>
                            <div className="pe-card-body">
                                <div
                                    className={`pe-anon-option${formData.isAnonymous ? ' on' : ''}`}
                                    onClick={() => toggle('isAnonymous')}
                                >
                                    <div className="pe-anon-icon">
                                        {formData.isAnonymous ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </div>
                                    <div className="pe-anon-text">
                                        <span className="pe-anon-title">Évaluation anonyme</span>
                                        <span className="pe-anon-desc">
                                            {formData.isAnonymous
                                                ? 'Actif — les évaluateurs s\'affichent comme « Anonyme »'
                                                : 'Inactif — les évaluateurs sont identifiés'
                                            }
                                        </span>
                                    </div>
                                    <button
                                        className={`pe-source-toggle${formData.isAnonymous ? ' accent' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); toggle('isAnonymous'); }}
                                        type="button"
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                </div>
            )}

            {/* ════ TAB CONSULT ════ */}
            {activeTab === 'consult' && (
                <div className="pe-consult-layout">

                    {/* Sidebar */}
                    <div className="pe-sidebar">
                        <div className="pe-sidebar-search">
                            <Search size={13} className="pe-sidebar-search-icon" />
                            <input
                                type="text"
                                placeholder="Rechercher un collaborateur..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pe-sidebar-input"
                            />
                        </div>
                        <div className="pe-sidebar-list">
                            {filteredUsers?.map(u => (
                                <div
                                    key={u.id}
                                    onClick={() => setSelectedUserId(u.id.toString())}
                                    className={`pe-user-item${selectedUserId === u.id.toString() ? ' selected' : ''}`}
                                >
                                    <div className="pe-user-avatar">
                                        {u.firstName?.[0]}{u.lastName?.[0]}
                                    </div>
                                    <div className="pe-user-info">
                                        <span className="pe-user-name">{u.firstName} {u.lastName}</span>
                                        <span className="pe-user-job">{u.jobName || '—'}</span>
                                    </div>
                                    <ChevronRight size={13} className="pe-user-arrow" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Main panel */}
                    <div className="pe-consult-panel">
                        {!selectedCampagneId ? (
                            <div className="pe-empty">
                                <div className="pe-empty-icon"><BarChart2 size={22} /></div>
                                <p className="pe-empty-title">Aucune campagne sélectionnée</p>
                                <p className="pe-empty-desc">Choisissez une campagne dans la barre en haut pour consulter les assignations.</p>
                            </div>
                        ) : !selectedUserId ? (
                            <div className="pe-empty">
                                <div className="pe-empty-icon"><UserIcon size={22} /></div>
                                <p className="pe-empty-title">Sélectionnez un collaborateur</p>
                                <p className="pe-empty-desc">Choisissez un collaborateur dans la liste pour voir ses évaluateurs assignés.</p>
                            </div>
                        ) : loadingEvals ? (
                            <div className="pe-loading">
                                <div className="pe-spinner" />
                                Chargement des évaluations...
                            </div>
                        ) : !hasPeerSkills ? (
                            <div className="pe-error-block">
                                <AlertCircle size={16} className="pe-error-icon" />
                                <p className="pe-error-text">
                                    Ce profil n'a aucune compétence paramétrée pour le mode 360°. Vérifiez les associations de compétences.
                                </p>
                            </div>
                        ) : peerAssignments?.length > 0 ? (
                            <div>
                                <div className="pe-eval-header">
                                    <span className="pe-eval-title">Évaluateurs assignés</span>
                                    <span className="pe-eval-count">{peerAssignments.length}</span>
                                </div>
                                <div className="pe-eval-grid">
                                    {peerAssignments.map(evalItem => (
                                        <div key={evalItem.id} className="pe-eval-card">
                                            <div className="pe-eval-avatar">
                                                {evalItem.evaluateurFullName?.[0] || '?'}
                                            </div>
                                            <div className="pe-eval-info">
                                                <span className="pe-eval-name">{evalItem.evaluateurFullName}</span>
                                                <span className="pe-eval-job">{evalItem.evaluateurJobTitle || 'Poste inconnu'}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="pe-empty">
                                <div className="pe-empty-icon"><Zap size={22} /></div>
                                <p className="pe-empty-title">Aucune assignation trouvée</p>
                                <p className="pe-empty-desc">Le tirage au sort n'a pas encore été lancé pour ce collaborateur.</p>
                            </div>
                        )}
                    </div>

                </div>
            )}

        </div>
    );
};

export default ParamEvaluation360;
